import { NgClass } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ADMIN_ROUTE_PATHS } from 'core/constants/admin-route-paths';
import { GroupNavMode } from 'core/models/platform-settings.model';
import { Role } from 'core/models/role.model';
import { PlatformSearchService } from 'core/services/platform-search.service';
import {
  PlatformSearchEntry,
  buildPlatformSearchIndex,
  filterPlatformSearchEntries,
} from 'core/utils/platform-search.util';
import { EmptyState } from 'ui/empty-state/empty-state';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';

@Component({
  selector: 'app-platform-search-dialog',
  standalone: true,
  imports: [FormsModule, NgClass, EmptyState],
  templateUrl: './platform-search-dialog.html',
})
export class PlatformSearchDialog {
  private readonly router = inject(Router);
  private readonly platformSearch = inject(PlatformSearchService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly entityQueryChanges = new Subject<string>();

  readonly open = input(false);
  readonly role = input<Role | null>(null);
  readonly groupNavMode = input<GroupNavMode>('submenu');

  readonly closed = output<void>();

  private readonly searchInput =
    viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly resultsScroll =
    viewChild<ElementRef<HTMLElement>>('resultsScroll');

  protected readonly query = signal('');
  protected readonly activeIndex = signal(0);
  protected readonly entityTotal = signal(0);
  protected readonly entitySearchReady = signal(false);
  protected readonly standaloneNgModel = { standalone: true };

  protected readonly catalog = computed(() =>
    buildPlatformSearchIndex(this.role(), this.groupNavMode()),
  );

  protected readonly results = computed(() =>
    filterPlatformSearchEntries(this.catalog(), this.query()),
  );

  protected readonly hasQuery = computed(() => this.query().trim().length > 0);

  /**
   * CTA so para hits da busca global na base (API).
   * Rotas de menu (Listar/Cadastrar/Lixeira) nao disparam este botao.
   */
  protected readonly showFullResultsCta = computed(
    () =>
      this.hasQuery() &&
      this.entitySearchReady() &&
      this.entityTotal() > 0 &&
      !this.platformSearch.loadError(),
  );

  constructor() {
    this.entityQueryChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((q) => {
          this.entitySearchReady.set(false);
          this.entityTotal.set(0);
          return this.platformSearch.search(q);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        const total = result.groups
          .filter((group) => group.type !== 'page')
          .reduce((sum, group) => sum + group.items.length, 0);
        this.entityTotal.set(total);
        this.entitySearchReady.set(true);
      });

    effect(() => {
      if (!this.open()) {
        return;
      }

      this.query.set('');
      this.activeIndex.set(0);
      this.entityTotal.set(0);
      this.entitySearchReady.set(false);
      this.entityQueryChanges.next('');

      queueMicrotask(() => {
        this.searchInput()?.nativeElement.focus();
      });
    });

    effect(() => {
      const count = this.results().length;
      if (this.activeIndex() >= count) {
        this.activeIndex.set(count > 0 ? count - 1 : 0);
      }
    });
  }

  protected onQueryChange(value: string): void {
    this.query.set(value);
    this.activeIndex.set(0);
    this.entityQueryChanges.next(value.trim());
  }

  protected onClearQuery(): void {
    this.onQueryChange('');
    queueMicrotask(() => {
      this.searchInput()?.nativeElement.focus();
    });
  }

  protected onBackdropClick(): void {
    this.closed.emit();
  }

  protected onCancel(): void {
    this.closed.emit();
  }

  protected selectEntry(entry: PlatformSearchEntry): void {
    void this.router.navigate([entry.route], {
      queryParams: entry.queryParams,
    });
    this.closed.emit();
  }

  protected openFullResults(): void {
    if (!this.showFullResultsCta()) {
      return;
    }

    const q = this.query().trim();
    if (!q) {
      return;
    }

    void this.router.navigate([ADMIN_ROUTE_PATHS.platformSearch], {
      queryParams: { q },
    });
    this.closed.emit();
  }

  protected onResultMouseEnter(index: number): void {
    this.activeIndex.set(index);
  }

  private moveActive(delta: number, total: number): void {
    this.activeIndex.update((index) => (index + delta + total) % total);
    this.scrollActiveIntoView();
  }

  private scrollActiveIntoView(): void {
    queueMicrotask(() => {
      const container = this.resultsScroll()?.nativeElement;
      if (!container) {
        return;
      }

      const active = container.querySelector<HTMLElement>(
        `[data-search-index="${this.activeIndex()}"]`,
      );
      active?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  @HostListener('document:keydown', ['$event'])
  protected handleKeydown(event: KeyboardEvent): void {
    if (!this.open()) {
      return;
    }

    const results = this.results();

    if (event.key === 'Escape') {
      event.preventDefault();
      this.onCancel();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (results.length === 0) {
        return;
      }
      this.moveActive(1, results.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (results.length === 0) {
        return;
      }
      this.moveActive(-1, results.length);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (event.shiftKey) {
        this.openFullResults();
        return;
      }

      const entry = results[this.activeIndex()];
      if (entry) {
        this.selectEntry(entry);
        return;
      }

      this.openFullResults();
    }
  }
}
